import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { type DefaultValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AccordionContentBox } from '~/components/AccordionContentBox';
import { Form, type FormProps } from '~/components/Form';
import { FormFieldDropdown, FormFieldInput } from '~/components/FormField';
import { FormFieldToggle } from '~/components/FormField/components/FormFieldToggle';
import { getI18nErrorMap } from '~/lib/getI18nErrorMap';
import { FormFieldLinkInput } from '~/modules/settings/components/FormField/components/FormFieldLinkInput';
import { SelectDistributor } from '~/modules/settings/components/SelectDistributor';
import { SelectLabelGroups } from '~/modules/settings/components/SelectLabelGroups';
import { SelectLabels } from '~/modules/settings/components/SelectLabels';
import { SelectPresentationLabel } from '~/modules/settings/components/SelectPresentationLabel';
import { DEAL_REGION_SCOPES_OPTIONS } from '~settings/constants';
import { DistributionGroups } from '~settings/modules/label/components/DistributionGroups';

import { SelectDealOwner } from '../SelectDealOwner';
import type { LabelFormSchema } from './schema';
import { labelFormSchema } from './schema';

type LabelFormProps = Omit<
  FormProps<LabelFormSchema>,
  'formHook' | 'schema'
> & {
  isActive?: boolean;
};

export function LabelForm({
  isActive,
  values,
  onSubmit,
  ...formProps
}: LabelFormProps) {
  const { t } = useTranslation('settings');
  const isEditMode = !!values;

  const form = useForm<LabelFormSchema>({
    defaultValues: {
      active: isActive ?? true,
      labelName: '',
      labelGroups: [],
      weaLabelCode: '',
      usLabelCode: '',
      dealOwner: '',
      dealRegionScope: undefined,
      wmiLabelCode: '',
      marketingCompany: '',
      repertoireOwner: '',
      wbsElementNumber: '',
      dealId: '',
      dealScope: '',
      scopeId: '',
      profitCenter: '',
      pmo: '',
      gvlCode: '',
      labelPresentation: '00CREW', // todo remove after https://wmggt.atlassian.net/browse/PERMS-1069 is done
      distributorId: '',
      gpiNumber: '',
      distributionGroups: [],
      facebookReference: '',
      twitterReference: '',
      youtubeReference: '',
      instagramReference: '',
      website: '',
      allowTimedRelease: false,
      asylum: false,
      directPassQc: false,
      parentLabel: undefined,
      labelIsrcPrefix: '',
    } as DefaultValues<LabelFormSchema>,
    values,
    resolver: zodResolver(labelFormSchema, {
      errorMap: getI18nErrorMap((s) => t(`label.validations.${s}`)),
    }),
  });

  // Updates the 'active' field if the prop changes because the Toggle is outside of the form
  useEffect(() => {
    if (isActive !== undefined) {
      form.setValue('active', isActive);
    }
  }, [isActive, form]);

  const [dealId, scopeId] = form.watch(['dealId', 'scopeId']);

  useEffect(() => {
    async function populateDealScopeIfValid() {
      const dealIdFormat = labelFormSchema.shape.dealId.safeParse(dealId);
      const scopeIdFormat = labelFormSchema.shape.scopeId.safeParse(scopeId);

      if (dealIdFormat.success && scopeIdFormat.success) {
        form.setValue('dealScope', `${dealId}-${scopeId}`);
      } else {
        form.setValue('dealScope', '');
      }
    }

    void populateDealScopeIfValid();
  }, [form, dealId, scopeId]);

  const dealRegionScopeOptions = DEAL_REGION_SCOPES_OPTIONS.map((option) => ({
    value: option.value,
    label: t(`label.dealRegionScopes.${option.i18nKey}`),
  }));

  return (
    <Form
      data-testid="label-form"
      formHook={form}
      onSubmit={onSubmit}
      schema={labelFormSchema}
      {...formProps}
    >
      <div className="flex flex-col gap-md">
        <AccordionContentBox title={t('label.basicInformation')}>
          <div className="flex flex-wrap justify-between gap-md rounded-sm">
            <FormFieldInput
              name="labelName"
              className="min-w-xs flex-1"
              label={t('label.labelName')}
              placeholder={t('label.placeholder.labelName')}
            />
            <SelectLabels
              className="min-w-xs flex-1"
              name="parentLabel"
              multiselect={false}
              clearable
              labelType="parent_label"
              label={t('label.parentLabel')}
              placeholder={t('label.placeholder.parentLabel')}
              readOnly={isEditMode ? values?.parentLabel?.value === '' : false}
            />
            <SelectLabelGroups
              name="labelGroups"
              className="min-w-xs flex-1"
              label={t('label.labelGroups')}
              placeholder={t('label.placeholder.labelGroups')}
            />
          </div>
        </AccordionContentBox>
        <AccordionContentBox
          title={t('label.coreLabelTitle')}
          defaultOpen={false}
        >
          <div className="flex flex-col gap-md">
            <div className="flex flex-wrap justify-between gap-md rounded-sm">
              <FormFieldInput
                className="min-w-xs flex-1"
                name="weaLabelCode"
                label={t('label.weaLabelCode')}
                placeholder={t('label.placeholder.weaLabelCode')}
              />
              <FormFieldInput
                className="min-w-xs flex-1"
                name="usLabelCode"
                label={t('label.usLabelCode')}
                placeholder={t('label.placeholder.usLabelCode')}
              />
              <SelectDealOwner name="dealOwner" className="min-w-xs flex-1" />
            </div>
            <div className="flex flex-wrap justify-between gap-md rounded-sm">
              <FormFieldDropdown
                className="min-w-xs flex-1"
                name="dealRegionScope"
                label={t('label.dealRegionScope')}
                options={dealRegionScopeOptions}
                placeholder={t('label.placeholder.dealRegionScope')}
              />
              <FormFieldInput
                className="min-w-xs flex-1"
                name="wmiLabelCode"
                label={t('label.wmiLabelCode')}
                placeholder={t('label.placeholder.wmiLabelCode')}
              />
              <FormFieldInput
                className="min-w-xs flex-1"
                name="marketingCompany"
                label={t('label.marketingCompany')}
                placeholder={t('label.placeholder.marketingCompany')}
              />
            </div>
            <div className="flex flex-wrap justify-between gap-md rounded-sm">
              <FormFieldInput
                className="min-w-xs flex-1"
                name="repertoireOwner"
                label={t('label.repertoireOwner')}
                placeholder={t('label.placeholder.repertoireOwner')}
              />
              <FormFieldInput
                className="min-w-xs flex-1"
                name="wbsElementNumber"
                label={t('label.wbsElementNumber')}
                placeholder={t('label.placeholder.wbsElementNumber')}
              />
              <FormFieldInput
                className="min-w-xs flex-1"
                name="dealId"
                label={t('label.dealId')}
                placeholder={t('label.placeholder.dealId')}
              />
            </div>
            <div className="flex flex-wrap justify-between gap-md rounded-sm">
              <FormFieldInput
                className="min-w-xs flex-1"
                name="scopeId"
                label={t('label.scopeId')}
                placeholder={t('label.placeholder.scopeId')}
              />
              <FormFieldInput
                className="min-w-xs flex-1"
                name="dealScope"
                label={t('label.dealScope')}
                placeholder={t('label.placeholder.dealScope')}
                disabled
              />
              <FormFieldInput
                className="min-w-xs flex-1"
                name="gpiNumber"
                label={t('label.gpiNumber')}
                placeholder={t('label.placeholder.gpiNumber')}
              />
            </div>
            <div className="flex flex-wrap justify-between gap-md rounded-sm">
              <FormFieldInput
                className="min-w-xs flex-1"
                name="profitCenter"
                label={t('label.profitCenter')}
                placeholder={t('label.placeholder.profitCenter')}
              />
              <FormFieldInput
                className="min-w-xs flex-1"
                name="pmo"
                label={t('label.pmo')}
                placeholder={t('label.placeholder.pmo')}
              />
              <FormFieldInput
                className="min-w-xs flex-1"
                name="gvlCode"
                label={t('label.gvlCode')}
                placeholder={t('label.placeholder.gvlCode')}
              />
            </div>
            <div className="flex flex-wrap justify-between gap-md rounded-sm">
              <FormFieldInput
                className="min-w-xs flex-1"
                name="labelIsrcPrefix"
                label={t('label.isrcPrefix')}
                placeholder={t('label.placeholder.isrcPrefix')}
              />
              <SelectPresentationLabel
                className="min-w-xs flex-1"
                name="labelPresentation"
              />
              <SelectDistributor
                className="min-w-xs flex-1"
                name="distributorId"
                label={t('label.distributors')}
                placeholder={t('label.placeholder.distributors')}
              />
            </div>
          </div>
        </AccordionContentBox>
        <AccordionContentBox title={t('label.settings')} defaultOpen={false}>
          <div className="grid grid-cols-3 gap-md rounded-sm">
            <div className="flex gap-md">
              <FormFieldToggle
                label={t('label.directPass')}
                name="directPassQc"
                aria-label={t('label.directPass')}
                activeLabel={t('toggle.yes')}
                inactiveLabel={t('toggle.no')}
              />
            </div>
            <div className="flex gap-md">
              <FormFieldToggle
                label={t('label.allowTimedReleases')}
                name="allowTimedRelease"
                aria-label={t('label.allowTimedReleases')}
                activeLabel={t('toggle.yes')}
                inactiveLabel={t('toggle.no')}
              />
            </div>
            <div className="flex flex-col gap-md">
              <div className="flex gap-md">
                <FormFieldToggle
                  label={t('label.asylum')}
                  name="asylum"
                  aria-label={t('label.asylum')}
                  activeLabel={t('toggle.yes')}
                  inactiveLabel={t('toggle.no')}
                />
              </div>
            </div>
          </div>
        </AccordionContentBox>

        <DistributionGroups />
        <AccordionContentBox
          title={t('label.social')}
          defaultOpen={false}
          className="mb-xl"
        >
          <div className="flex flex-col gap-md">
            <div className="flex flex-wrap justify-between gap-md rounded-sm">
              <FormFieldLinkInput
                className="min-w-xs flex-1"
                name="facebookReference"
                label={t('label.facebook')}
                placeholder={t('label.placeholder.facebook')}
              />
              <FormFieldLinkInput
                className="min-w-xs flex-1"
                name="instagramReference"
                label={t('label.instagram')}
                placeholder={t('label.placeholder.instagram')}
              />
              <FormFieldLinkInput
                className="min-w-xs flex-1"
                name="youtubeReference"
                label={t('label.youtube')}
                placeholder={t('label.placeholder.youtube')}
              />
            </div>
            <div className="flex flex-wrap gap-md rounded-sm">
              <FormFieldLinkInput
                className="min-w-xs flex-1"
                name="twitterReference"
                label={t('label.x')}
                placeholder={t('label.placeholder.x')}
              />
              <FormFieldLinkInput
                className="min-w-xs flex-1"
                name="website"
                label={t('label.websiteURL')}
                placeholder={t('label.placeholder.websiteURL')}
              />
            </div>
          </div>
        </AccordionContentBox>
      </div>
    </Form>
  );
}

LabelForm.displayName = 'LabelForm';
